import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import ModernMarineLogo from '../../assets/images/modern-marine-logo-white.png';
import api from '../../config/api';

const RegisterContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${ModernMarineLogo}) center/contain no-repeat;
    opacity: 0.03;
    z-index: 1;
  }
`;

const RegisterCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['3xl']};
  backdrop-filter: blur(20px);
  box-shadow: ${theme.shadows.xl};
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 2;
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
  background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.base};
  transition: ${theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
  color: ${theme.colors.text.primary};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
`;

const LinkText = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const StyledLink = styled(Link)`
  color: ${theme.colors.primary.light};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.medium};
  
  &:hover {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get('/api/auth/registration-status')
      .then((res) => {
        if (!mounted) return;
        setRegistrationEnabled(!!res.data?.enabled);
      })
      .catch(() => {
        if (!mounted) return;
        setRegistrationEnabled(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (registrationEnabled === false) {
      toast.error('Account creation is currently disabled.');
      return;
    }
    // Require first and last name (backend enforces 2-50 chars)
    if (!formData.first_name || formData.first_name.trim().length < 2) {
      toast.error('Please enter your first name (min 2 characters)');
      return;
    }
    if (!formData.last_name || formData.last_name.trim().length < 2) {
      toast.error('Please enter your last name (min 2 characters)');
      return;
    }

    setLoading(true);

    const result = await register({
      email: formData.email,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim()
    });
    
    if (result.success) {
      toast.success('Check your email to activate your account.');
      navigate('/login');
    }
    
    setLoading(false);
  };

  return (
    <RegisterContainer>
      <RegisterCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <LogoContainer>
          <Logo src={ModernMarineLogo} alt="Modern Marine Logo" />
          <Title>Join BoatWise</Title>
          <Subtitle>Start monitoring your marine systems today</Subtitle>
        </LogoContainer>

        {registrationEnabled === false && (
          <div style={{
            padding: '12px 14px',
            borderRadius: theme.borderRadius.lg,
            border: '1px solid rgba(239, 68, 68, 0.35)',
            background: 'rgba(239, 68, 68, 0.10)',
            color: theme.colors.text.primary,
            marginBottom: theme.spacing.lg
          }}>
            Account creation is currently disabled.
          </div>
        )}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              name="first_name"
              type="text"
              required
              placeholder="Enter your first name"
              value={formData.first_name}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              name="last_name"
              type="text"
              required
              placeholder="Enter your last name"
              value={formData.last_name}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
            />
          </FormGroup>

          {/* Password is set during activation; hide fields to avoid confusion */}

          <SubmitButton
            type="submit"
            disabled={loading || registrationEnabled === false}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </SubmitButton>

          <LinkContainer>
            <LinkText>
              Already have an account?{' '}
            </LinkText>
            <StyledLink to="/login">
              Sign in here
            </StyledLink>
          </LinkContainer>
        </Form>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register;
